import PropTypes from 'prop-types';
import { map as _map } from 'lodash';

export const videoConfigProps = PropTypes.shape({
  autoPlay: PropTypes.bool,
  fallback: PropTypes.string,
  loop: PropTypes.bool,
  path: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]).isRequired,
  poster: PropTypes.string,
  posterWidth: PropTypes.number,
  preload: PropTypes.oneOf([
    `auto`,
    `metadata`,
    `none`,
  ]),
  height: PropTypes.number,
  width: PropTypes.number,
});

export const responsiveCarouselSlideProps = PropTypes.arrayOf(PropTypes.shape({
  imageAlt: PropTypes.string,
  imagePath: PropTypes.string.isRequired,
  heading: PropTypes.string,
  paragraph: PropTypes.string,
})).isRequired;

/* eslint-disable camelcase */
export const galleryProps = PropTypes.objectOf(PropTypes.shape({
  position: PropTypes.oneOf([
    `bottom-left`,
    `center`,
    `center-left`,
    `left`,
    `top-center`,
    `top-left`,
  ]).isRequired,
  variants: PropTypes.objectOf(PropTypes.shape({
    image_path: PropTypes.string.isRequired,
    image_alt: PropTypes.string,
    thumbnail_path: PropTypes.string.isRequired,
    thumbnail_alt: PropTypes.string,
    conditions: PropTypes.array,
  })),
})).isRequired;
/* eslint-enable camelcase */

export const productCtaCardsProps = PropTypes.shape({
  buttonCta: PropTypes.string.isRequired,
  key: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  imagePath: PropTypes.string.isRequired,
  paragraph: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtext: PropTypes.string.isRequired,
});

export const carouselCardsProps = PropTypes.shape({
  imagePath: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
});

export const productCardsProps = PropTypes.shape({
  analytics: PropTypes.shape({
    productName: PropTypes.string.isRequired,
    productCategory: PropTypes.string.isRequired,
  }),
  imageAlt: PropTypes.string.isRequired,
  imagePath: PropTypes.string.isRequired,
  paragraph: PropTypes.string.isRequired,
  pathKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
});

export const reviewProps = PropTypes.shape({
  date: PropTypes.string,
  location: PropTypes.string,
  name: PropTypes.string,
  paragraph: PropTypes.string,
  rating: PropTypes.number,
  title: PropTypes.string,
  verified: PropTypes.bool,
});

export const reviewHubProps = PropTypes.shape({
  ContentLocale: PropTypes.string,
  IncentivizedReview: PropTypes.bool,
  IncentivizedType: PropTypes.string,
  Partners: PropTypes.string,
  Pets: PropTypes.string,
  Rating: PropTypes.number,
  ReviewText: PropTypes.string,
  SubmissionTime: PropTypes.string,
  Title: PropTypes.string,
  UserLocation: PropTypes.string,
  UserNickname: PropTypes.string,
  age: PropTypes.number,
  avatar: PropTypes.number,
  hoursOfSleep: PropTypes.string,
  id: PropTypes.string,
});

export const staticReviewProps = PropTypes.shape({
  age: PropTypes.number,
  avatar: PropTypes.number,
  hoursOfSleep: PropTypes.number,
  Rating : PropTypes.number,
  ReviewText: PropTypes.string,
  SubmissionTime: PropTypes.string,
  Title: PropTypes.string,
  UserLocation: PropTypes.string,
  UserNickname: PropTypes.string,
  verified: PropTypes.bool,
});

export const dynamicReviewProps = PropTypes.shape({
  age: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  avatar: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  hoursOfSleep: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  Rating : PropTypes.number,
  ReviewText: PropTypes.string,
  SubmissionTime: PropTypes.string,
  Title: PropTypes.string,
  UserLocation: PropTypes.string,
  UserNickname: PropTypes.string,
  verified: PropTypes.bool,
});

export const imageConfigProps = PropTypes.oneOfType([
  PropTypes.arrayOf(
    PropTypes.shape({
      max: PropTypes.number,
      media: PropTypes.string,
      min: PropTypes.number.isRequired,
      path: PropTypes.string.isRequired,
      sizes: PropTypes.string.isRequired,
    })
  ),
  PropTypes.shape({
    max: PropTypes.number,
    min: PropTypes.number.isRequired,
    path: PropTypes.string.isRequired,
    sizes: PropTypes.string.isRequired,
  }),
]);

export const financeCopy = PropTypes.shape({
  affirmPromoId: PropTypes.string,
  heading: PropTypes.string.isRequired,
  copy: PropTypes.string.isRequired,
  callouts: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    copy: PropTypes.string.isRequired,
    linkText: PropTypes.string,
  })),
});

export const storeListingProps = {
  address: PropTypes.string,
  city: PropTypes.string,
  exclusive: PropTypes.bool,
  hasProductCore: PropTypes.bool,
  hasProductEssential: PropTypes.bool,
  hasProductPillows: PropTypes.bool,
  hasProductProtector: PropTypes.bool,
  hasProductSheets: PropTypes.bool,
  name: PropTypes.string,
  phone: PropTypes.string,
  provice: PropTypes.string,
  state: PropTypes.string,
  zipcode: PropTypes.string,
};

export const tweetBlockProps = {
  body: PropTypes.string.isRequired,
  user: PropTypes.shape({
    avatar: PropTypes.string,
    name: PropTypes.string,
    handle: PropTypes.string,
  }),
};

const reviewComposerProducts = PropTypes.shape({
  heading: PropTypes.string,
  titlePlaceholder: PropTypes.string,
  reviewPlaceholder: PropTypes.string,
});

export const reviewComposerProps = PropTypes.shape({
  avatars: PropTypes.arrayOf(PropTypes.string),
  autoSubmitAvatars: PropTypes.arrayOf(PropTypes.string),
  products: PropTypes.shape({
    casper: reviewComposerProducts,
    casperWave: reviewComposerProducts,
    casperEssential: reviewComposerProducts,
    pillow: reviewComposerProducts,
    sheets: reviewComposerProducts,
    dogbed: reviewComposerProducts,
    duvet: reviewComposerProducts,
    protector: reviewComposerProducts,
  }),
  headingPageTwo: PropTypes.string,
  paragraphPageTwo: PropTypes.string,
  buttonText: PropTypes.string,
  linkText: PropTypes.string,
  errorMessage: PropTypes.string,
  ratingField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
  }),
  nameField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
    placeholder: PropTypes.string,
  }),
  dogNameField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
    placeholder: PropTypes.string,
  }),
  dogWeightField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })),
  }),
  dogBedSizeField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })),
  }),
  dogPositionField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })),
  }),
  ageField: PropTypes.shape({
    label: PropTypes.string,
  }),
  emailField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
    placeholder: PropTypes.string,
  }),
  cityField: PropTypes.shape({
    label:PropTypes.string,
    placeholder: PropTypes.string,
  }),
  stateField: PropTypes.shape({
    label: PropTypes.string,
  }),
  titleField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
  }),
  reviewField: PropTypes.shape({
    label: PropTypes.string,
    error: PropTypes.string,
  }),
  questionsPageTwo: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      type: PropTypes.string,
      label:PropTypes.string,
    })),
  })),
});

/*
  Simplified version of the prop-types internal function of the same name.
  @link https://github.com/facebook/prop-types/blob/8abb8d73c6365aac2b9f508a9c49b0934de78f0a/factoryWithTypeCheckers.js#L150
*/
const createChainableTypeChecker = (validate) => {
  const checkType = function(isRequired, props, propName, componentName, location, propFullName) {
    if (props[propName] == null) {
      if (isRequired) {
        const errorValue = props[propName] === null ? `null` : `undefined`;

        return new Error(`The ${location} '${propFullName}' is marked as required in '${componentName}', but its value is '${errorValue}'.`);
      }

      return null;
    } else {
      return validate(props, propName, componentName, location, propFullName);
    }
  };

  const chainedCheckType = checkType.bind(null, false);
  chainedCheckType.isRequired = checkType.bind(null, true);

  return chainedCheckType;
};

export const customPropTypes = {
  /*
    Custom validator function which ensures that the prop's valud is one of the values
    in the provided `keyObject`.

    Example:
    MyComponent.propTypes = {
      validatedProp: customPropTypes.oneOfObjectProps({
        key1: `value1`,
        key2: `value2`,
      }),
    };

    @param: {?object} keyObject The object containing the valid prop values.
    @return {?function}
  */
  oneOfObjectProps(keyObject) {
    return createChainableTypeChecker((props, propName, componentName, location) => {
      if (!keyObject || typeof keyObject !== `object` || !Object.keys(keyObject).length) {
        return new Error(`A valid 'keyObject' value wasn't passed to 'customPropTypes.oneOfObjectProps' for the '${propName} prop in ${componentName}.`);
      }

      const propTypesToTest = { [propName]: PropTypes.oneOf(_map(Object.keys(keyObject), (key) => keyObject[key])) };
      const propToTest = { [propName]: props[propName] };

      PropTypes.checkPropTypes(propTypesToTest, propToTest, componentName, location);

      return null;
    });
  },

  /*
    Custom validator function which performs it's validation based upon the value
    of the specified `keyPropName` and the options supplied in `typeOptions`.

    Example:
    MyComponent.propTypes = {
      keyProp: PropTypes.oneOf(`x`, `y`).isRequired,
      validatedProp: customPropTypes.oneOfTypeForKeyProp(`keyProp`, {
        // If `keyProp`'s value is 'x', then `validatedProp` must be a string.
        x: PropTypes.string,

        // If `keyProp`'s value is 'y', then `validatedProp` must be an array of numbers.
        y: PropTypes.arrayOf(PropTypes.number),
      }).isRequired,
    };

    @param {?string} keyPropName The name of the prop being used as the key to this validation.
    @param {?object} keyValidators An object mapping the key value to the expected prop type.
    @return {?function}
  */
  oneOfTypeForKeyProp(keyPropName, keyValidators) {
    return createChainableTypeChecker((props, propName, componentName, location) => {
      const keyPropValue = props[keyPropName];

      if (keyPropValue === null) {
        return new Error(`The key prop hasn't been provided for the '${propName}' prop in '${componentName}'`);
      }

      if (!keyValidators.hasOwnProperty(keyPropValue)) {
        const acceptedKeys = Object.keys(keyValidators).join(`, `);

        return new Error(`The key value '${keyPropValue}' doesn't match with one of the expected keys. Options are: ${acceptedKeys}`);
      }

      // PropTypes are defined as an object, so we create one for the type definitions and the value
      // in order to perform the type check.
      const propTypesToTest = { [propName]: keyValidators[keyPropValue] };
      const propToTest = { [propName]: props[propName] };

      PropTypes.checkPropTypes(propTypesToTest, propToTest, componentName, location);

      return null;
    });
  },
};
